import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Card,
  Descriptions,
  Typography,
  Divider,
  Spin,
} from "antd";
import {
  PlusCircleTwoTone,
  MinusCircleTwoTone,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDisciplinaryIncidentsByParent,
} from "../../store/disciplinaryIncidentSlice";
import {
  fetchDisciplinaryIncidentActionsByStudent,
} from "../../store/disciplinaryIncidentActionSlice";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";
import dayjs from "dayjs";

const { Title } = Typography;

const DisciplinaryIncidentPage = () => {
  const dispatch = useDispatch();
  const [isParent, setIsParent] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [userId, setUserId] = useState("");
  const [data, setData] = useState([]);

  const { isAuthenticated } = useSelector((state) => state.auth);
  const { incidents = [], loading: parentLoading } = useSelector(
    (state) => state.disciplinaryIncident
  );
  const {
    items: incidentActions = [],
    status: studentStatus,
  } = useSelector((state) => state.disciplinaryIncidentAction);

  useTokenExpiryCheck();

  useEffect(() => {
    if (isAuthenticated) {
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const roles = userInfo?.roles || [];
      const uid = userInfo?.userId || "";
      setUserId(uid);

      if (roles.includes("Parent")) {
        setIsParent(true);
        setIsStudent(false);
        dispatch(fetchDisciplinaryIncidentsByParent(uid));
      } else if (roles.includes("Student")) {
        setIsStudent(true);
        setIsParent(false);
        dispatch(fetchDisciplinaryIncidentActionsByStudent(uid));
      }
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isStudent) setData(incidentActions);
    else if (isParent) setData(incidents);
  }, [incidentActions, incidents, isStudent, isParent]);

  const formatTagDate = (dateStr) => {
    if (!dateStr) return <Tag color="default">N/A</Tag>;
    return <Tag color="blue">{dayjs(dateStr).format("DD MMM YYYY, hh:mm A")}</Tag>;
  };

  const getStatusTag = (value, mapping) => {
    const status = mapping[value] || "Unknown";
    const color =
      value === 0 ? "default" :
      value === 1 ? "orange" :
      value === 2 ? "green" :
      "gray";
    return <Tag color={color}>{status}</Tag>;
  };

  const columns = [
    {
      title: "Student",
      dataIndex: "studentName",
      key: "studentName",
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Action",
      dataIndex: "actionName",
      key: "actionName",
      render: (text) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: "Parental Hearing",
      dataIndex: "parentalHearingStatus",
      key: "parentalHearingStatus",
      render: (val) =>
        getStatusTag(val, {
          0: "Not applied",
          1: "Scheduled",
          2: "Completed",
        }),
    },
    {
      title: "Counseling",
      dataIndex: "counclingStatus",
      key: "counclingStatus",
      render: (val) =>
        getStatusTag(val, {
          0: "Not applied",
          1: "Ongoing",
          2: "Completed",
        }),
    },
    {
      title: "Hearing",
      dataIndex: "hearingStatus",
      key: "hearingStatus",
      render: (val) =>
        getStatusTag(val, {
          0: "Not applied",
          1: "Scheduled",
          2: "Completed",
        }),
    },
  ];

  const expandedRowRender = (record) => (
    <Descriptions
      bordered
      column={2}
      size="small"
      title="More Details"
      style={{ background: "#fff" }}
    >
      <Descriptions.Item label="Parental Hearing Date">
        {formatTagDate(record.parentalHearingUpdateDate)}
      </Descriptions.Item>
      <Descriptions.Item label="Counseling Date">
        {formatTagDate(record.counclingUpdateDate)}
      </Descriptions.Item>
      <Descriptions.Item label="Hearing Date">
        {formatTagDate(record.hearingUpdateDate)}
      </Descriptions.Item>
    </Descriptions>
  );

  const customExpandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <MinusCircleTwoTone
        onClick={(e) => onExpand(record, e)}
        style={{ fontSize: 16 }}
      />
    ) : (
      <PlusCircleTwoTone
        onClick={(e) => onExpand(record, e)}
        style={{ fontSize: 16 }}
      />
    );

  const loading = isStudent ? studentStatus === "loading" : parentLoading;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        Disciplinary Incident Records
      </Title>

      <Card bordered>
        {loading ? (
          <Spin size="large" />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) =>
              isStudent ? record.incidentActionID : record.incidentID
            }
            expandable={{
              expandedRowRender,
              expandIcon: customExpandIcon,
              rowExpandable: () => true,
            }}
            pagination={{ pageSize: 6 }}
          />
        )}
      </Card>
    </div>
  );
};

export default DisciplinaryIncidentPage;
